// ─── Page Templates ────────────────────────────────────────────────────────────
// System-defined page templates used when creating a new CMS page.
// Templates are defined in code (no DB) and pre-populate the block tree.
//
// _templateId: temporary stable id used to wire child blocks to parent rows.
//              It is resolved to a real UUID by expandTemplateBlocks() before
//              being sent to the API.

export interface BlockTemplate {
  _templateId?: string; // stable ref for parent/child wiring
  type: string;         // block definition slug (resolved to blockDefinitionId at save time)
  sortOrder: number;
  props: Record<string, unknown>;
  parentBlockId?: string; // references _templateId of parent row
}

export interface PageTemplate {
  id: string;
  name: string;
  description: string;
  icon: string; // Lucide icon name
  blocks: BlockTemplate[];
}

export const PAGE_TEMPLATES: PageTemplate[] = [
  {
    id: 'blank',
    name: 'Blank',
    description: 'Start from scratch with an empty page',
    icon: 'FileText',
    blocks: [],
  },
  {
    id: 'landing',
    name: 'Landing Page',
    description: 'Hero section, feature grid, and call-to-action',
    icon: 'Rocket',
    blocks: [
      {
        type: 'hero',
        sortOrder: 0,
        props: { title: 'Welcome', subtitle: '', ctaLabel: 'Get started', ctaUrl: '#' },
      },
      {
        type: 'feature_grid',
        sortOrder: 1,
        props: { title: 'Features', items: [] },
      },
      {
        type: 'banner',
        sortOrder: 2,
        props: { text: 'Ready to begin?', ctaLabel: 'Start now', ctaUrl: '#' },
      },
    ],
  },
  {
    id: 'two-column',
    name: 'Two Column',
    description: 'Rich text on the left, image on the right',
    icon: 'Columns2',
    blocks: [
      {
        _templateId: 'row-1',
        type: 'row',
        sortOrder: 0,
        props: { columnCount: 2, columnWidths: ['1/2', '1/2'], gap: 'md', verticalAlign: 'top' },
      },
      {
        type: 'rich_text',
        sortOrder: 0,
        props: { content: '<h2>Your heading</h2><p>Add your content here...</p>', _col: 0 },
        parentBlockId: 'row-1',
      },
      {
        type: 'image',
        sortOrder: 1,
        props: { src: '', alt: '', _col: 1 },
        parentBlockId: 'row-1',
      },
    ],
  },
  {
    id: 'feature-showcase',
    name: 'Feature Showcase',
    description: 'Hero banner with three-column feature highlights',
    icon: 'LayoutTemplate',
    blocks: [
      {
        type: 'hero',
        sortOrder: 0,
        props: { title: 'Our Features', subtitle: 'Everything you need', ctaLabel: '', ctaUrl: '' },
      },
      {
        _templateId: 'row-features',
        type: 'row',
        sortOrder: 1,
        props: { columnCount: 3, columnWidths: ['1/3', '1/3', '1/3'], gap: 'md', verticalAlign: 'top' },
      },
      {
        type: 'text',
        sortOrder: 0,
        props: { text: 'Feature one description', _col: 0 },
        parentBlockId: 'row-features',
      },
      {
        type: 'text',
        sortOrder: 1,
        props: { text: 'Feature two description', _col: 1 },
        parentBlockId: 'row-features',
      },
      {
        type: 'text',
        sortOrder: 2,
        props: { text: 'Feature three description', _col: 2 },
        parentBlockId: 'row-features',
      },
    ],
  },
  {
    id: 'article',
    name: 'Article',
    description: 'Cover image followed by rich text content',
    icon: 'BookOpen',
    blocks: [
      {
        type: 'image',
        sortOrder: 0,
        props: { src: '', alt: '', caption: '' },
      },
      {
        type: 'rich_text',
        sortOrder: 1,
        props: { content: '<p>Start writing here...</p>' },
      },
    ],
  },
  {
    id: 'loyalty-hub',
    name: 'Loyalty Hub',
    description: 'Member points, rewards, and tier status',
    icon: 'Star',
    blocks: [
      { type: 'loyalty_points', sortOrder: 0, props: {} },
      { type: 'loyalty_tier', sortOrder: 1, props: {} },
      { type: 'loyalty_rewards', sortOrder: 2, props: {} },
    ],
  },
  {
    id: 'contact',
    name: 'Contact / Form',
    description: 'Introductory text with a contact form',
    icon: 'Mail',
    blocks: [
      {
        type: 'hero',
        sortOrder: 0,
        props: { title: 'Get in touch', subtitle: 'Fill the form below and we will be in touch shortly.' },
      },
      {
        type: 'form',
        sortOrder: 1,
        props: { formId: null },
      },
    ],
  },
  {
    id: 'team',
    name: 'About / Team',
    description: 'Introductory text and team card grid',
    icon: 'Users',
    blocks: [
      {
        type: 'rich_text',
        sortOrder: 0,
        props: { content: '<h2>About us</h2><p>Our story...</p>' },
      },
      {
        type: 'card_grid',
        sortOrder: 1,
        props: { columns: 3, cards: [] },
      },
    ],
  },
];
